import type { PlaybackLifecycleEvent } from "./lifecycle";
import { shouldRegenerateManagedStream } from "./streamPlan";
import { adjustMediaVolumeBySliderStep } from "./volumeCurve";

export type PlayerCoordinatorCommand =
  | { type: "CLEAR_RECOVERY_NOTICE" }
  | { type: "MARK_SOURCE_PLAYING" }
  | { type: "SESSION_START" }
  | { type: "SESSION_PROGRESS" }
  | { type: "SESSION_STOP" }
  | { type: "SEEK"; seconds: number; reportIntent: boolean }
  | { type: "SEEK_DECODER"; seconds: number; reportIntent: boolean }
  | { type: "CLEAR_SEEK" }
  | { type: "SHOW_CONTROLS" }
  | { type: "PLAY_NEXT" }
  | { type: "SET_RECOVERY_NOTICE"; message: string }
  | { type: "SET_SERVER_SEEK"; itemId?: string; seconds?: number }
  | {
      type: "BEGIN_SOURCE";
      sourceId: string;
      recovering: boolean;
      positionSeconds: number;
      continuePausedSession: boolean;
    }
  | {
      type: "RETRY_SOURCE";
      positionSeconds: number;
      continuePausedSession: boolean;
    }
  | { type: "RESET_TRACKS" }
  | { type: "CLOSE_MENU" }
  | { type: "REFETCH_PLAYBACK_INFO" }
  | { type: "SHOW_PLAYBACK_PULSE"; kind: "play" | "pause" };

export type PlayerCoordinatorStep =
  | { type: "LIFECYCLE"; event: PlaybackLifecycleEvent }
  | { type: "COMMAND"; command: PlayerCoordinatorCommand };

export type DecoderEventDecision = {
  steps: PlayerCoordinatorStep[];
};

const ignoredDecision = (): DecoderEventDecision => ({ steps: [] });
const lifecycle = (event: PlaybackLifecycleEvent): PlayerCoordinatorStep => ({
  type: "LIFECYCLE",
  event,
});
const command = (value: PlayerCoordinatorCommand): PlayerCoordinatorStep => ({
  type: "COMMAND",
  command: value,
});

export function planDecoderLoad(recovering: boolean): DecoderEventDecision {
  return { steps: [lifecycle({ type: "LOAD", recovering })] };
}

export function planDecoderReady(input: {
  authoritative: boolean;
  decoderDurationSeconds: number;
  streamStartSeconds: number;
  seekTarget?: number;
  continuePausedSession: boolean;
  sessionStarted: boolean;
}): DecoderEventDecision {
  if (!input.authoritative) return ignoredDecision();
  const duration = Math.max(
    0,
    (Number.isFinite(input.streamStartSeconds) ? input.streamStartSeconds : 0) +
      (Number.isFinite(input.decoderDurationSeconds) ? input.decoderDurationSeconds : 0),
  );
  const steps: PlayerCoordinatorStep[] = [
    lifecycle({ type: "SET_DURATION", value: duration }),
    lifecycle({ type: "READY" }),
  ];
  if (input.streamStartSeconds > 0) {
    steps.push(lifecycle({ type: "SET_POSITION", value: input.streamStartSeconds }));
  }
  steps.push(command({ type: "CLEAR_RECOVERY_NOTICE" }));
  if (input.seekTarget !== undefined) {
    steps.push(command({ type: "SEEK", seconds: input.seekTarget, reportIntent: false }));
  }
  if (input.continuePausedSession) steps.push(command({ type: "SESSION_START" }));
  else if (input.sessionStarted) steps.push(command({ type: "SESSION_PROGRESS" }));
  return { steps };
}

export function planDecoderPlayingChange(input: {
  authoritative: boolean;
  playing: boolean;
  paused: boolean;
  sessionStarted: boolean;
}): DecoderEventDecision {
  if (!input.authoritative) return ignoredDecision();
  if (input.playing) {
    return {
      steps: [
        command({ type: "MARK_SOURCE_PLAYING" }),
        lifecycle({ type: "PLAYING" }),
        ...(!input.sessionStarted
          ? [command({ type: "SESSION_START" })]
          : []),
      ],
    };
  }
  if (input.paused) return ignoredDecision();
  return {
    steps: [
      lifecycle({ type: "SET_PAUSED", value: true }),
      ...(input.sessionStarted
        ? [command({ type: "SESSION_PROGRESS" })]
        : []),
      command({ type: "SHOW_CONTROLS" }),
    ],
  };
}

export function planDecoderProgress(input: {
  authoritative: boolean;
  accepted: boolean;
  absolutePositionSeconds: number;
  absoluteBufferedSeconds?: number;
  reportProgress: boolean;
}): DecoderEventDecision {
  if (!input.authoritative) return ignoredDecision();
  const steps: PlayerCoordinatorStep[] = [];
  if (input.absoluteBufferedSeconds !== undefined) {
    steps.push(lifecycle({
      type: "SET_BUFFERED",
      value: input.absoluteBufferedSeconds,
    }));
  }
  if (!input.accepted || !Number.isFinite(input.absolutePositionSeconds)) {
    return { steps };
  }
  steps.push(lifecycle({
    type: "SET_POSITION",
    value: input.absolutePositionSeconds,
  }));
  if (input.reportProgress) steps.push(command({ type: "SESSION_PROGRESS" }));
  return { steps };
}

export function planDecoderBuffering(
  authoritative: boolean,
  active: boolean,
): DecoderEventDecision {
  return authoritative
    ? { steps: [lifecycle({ type: "BUFFERING", active })] }
    : ignoredDecision();
}

export function planDecoderEnded(input: {
  authoritative: boolean;
  timelineDuration: number;
  playNext: boolean;
}): DecoderEventDecision {
  if (!input.authoritative) return ignoredDecision();
  const steps: PlayerCoordinatorStep[] = [
    command({ type: "CLEAR_SEEK" }),
    lifecycle({ type: "SET_POSITION", value: input.timelineDuration }),
    lifecycle({ type: "ENDED" }),
    command({ type: "SESSION_PROGRESS" }),
    command({ type: "SESSION_STOP" }),
  ];
  if (input.playNext) steps.push(command({ type: "PLAY_NEXT" }));
  else {
    steps.push(lifecycle({ type: "SET_PAUSED", value: true }));
    steps.push(command({ type: "SHOW_CONTROLS" }));
  }
  return { steps };
}

export function planSourceSelection(input: {
  itemId: string;
  sourceId: string;
  managedHls: boolean;
  recovering: boolean;
  positionSeconds: number;
  paused: boolean;
  sessionStarted: boolean;
}): DecoderEventDecision {
  const steps: PlayerCoordinatorStep[] = [];
  if (input.sessionStarted) steps.push(command({ type: "SESSION_STOP" }));
  steps.push(command(input.managedHls
    ? {
        type: "SET_SERVER_SEEK",
        itemId: input.itemId,
        seconds: input.positionSeconds,
      }
    : { type: "SET_SERVER_SEEK" }));
  steps.push(command({
    type: "BEGIN_SOURCE",
    sourceId: input.sourceId,
    recovering: input.recovering,
    positionSeconds: input.positionSeconds,
    continuePausedSession: input.sessionStarted && input.paused,
  }));
  steps.push(lifecycle({ type: "RESET_MEDIA_CLOCK" }));
  steps.push(command({ type: "RESET_TRACKS" }));
  steps.push(command({ type: "CLOSE_MENU" }));
  return { steps };
}

export function planPlaybackFailure(input: {
  stale: boolean;
  message: string;
  sessionStarted: boolean;
  fallback?: {
    label: string;
    itemId: string;
    sourceId: string;
    managedHls: boolean;
    positionSeconds: number;
    paused: boolean;
  };
}): DecoderEventDecision {
  if (input.stale) return ignoredDecision();
  if (input.fallback) {
    const selection = planSourceSelection({
      itemId: input.fallback.itemId,
      sourceId: input.fallback.sourceId,
      managedHls: input.fallback.managedHls,
      recovering: true,
      positionSeconds: input.fallback.positionSeconds,
      paused: input.fallback.paused,
      sessionStarted: input.sessionStarted,
    });
    return {
      steps: [
        command({ type: "SET_RECOVERY_NOTICE", message: `Trying ${input.fallback.label}` }),
        ...selection.steps,
      ],
    };
  }
  return {
    steps: [
      ...(input.sessionStarted ? [command({ type: "SESSION_STOP" })] : []),
      lifecycle({ type: "FAILED", message: input.message }),
    ],
  };
}

export function planPlaybackRetry(input: {
  positionSeconds: number;
  paused: boolean;
  sessionStarted: boolean;
}): DecoderEventDecision {
  return {
    steps: [
      ...(input.sessionStarted ? [command({ type: "SESSION_STOP" })] : []),
      command({
        type: "RETRY_SOURCE",
        positionSeconds: input.positionSeconds,
        continuePausedSession: input.sessionStarted && input.paused,
      }),
      command({ type: "CLOSE_MENU" }),
      command({ type: "REFETCH_PLAYBACK_INFO" }),
    ],
  };
}

export function planTogglePaused(paused: boolean): DecoderEventDecision {
  const next = !paused;
  return {
    steps: [
      lifecycle({ type: "SET_PAUSED", value: next }),
      command({ type: "SHOW_PLAYBACK_PULSE", kind: next ? "pause" : "play" }),
      command({ type: "SHOW_CONTROLS" }),
      command({ type: "SESSION_PROGRESS" }),
    ],
  };
}

export function planToggleMuted(muted: boolean): DecoderEventDecision {
  return {
    steps: [
      lifecycle({ type: "SET_MUTED", value: !muted }),
      command({ type: "SHOW_CONTROLS" }),
      command({ type: "SESSION_PROGRESS" }),
    ],
  };
}

export function planAdjustVolume(
  volume: number,
  direction: -1 | 1,
): DecoderEventDecision {
  const next = adjustMediaVolumeBySliderStep(volume, direction);
  return {
    steps: [
      lifecycle({ type: "SET_VOLUME", value: next }),
      ...(next > 0
        ? [lifecycle({ type: "SET_MUTED", value: false })]
        : []),
      command({ type: "SHOW_CONTROLS" }),
      command({ type: "SESSION_PROGRESS" }),
    ],
  };
}

export function planSeekIntent(input: {
  requestedSeconds: number;
  timelineDuration: number;
  managedHls: boolean;
  streamStartSeconds: number;
  decoderDurationSeconds: number;
  decoderBufferedSeconds: number;
  itemId: string;
  reportIntent: boolean;
}): DecoderEventDecision {
  if (!Number.isFinite(input.requestedSeconds)) return ignoredDecision();
  const duration = Number.isFinite(input.timelineDuration)
    ? Math.max(0, input.timelineDuration)
    : 0;
  const target = Math.max(0, Math.min(duration, input.requestedSeconds));
  if (shouldRegenerateManagedStream({
    managedHls: input.managedHls,
    targetSeconds: target,
    streamStartSeconds: input.streamStartSeconds,
    decoderDurationSeconds: input.decoderDurationSeconds,
    decoderBufferedSeconds: input.decoderBufferedSeconds,
  })) {
    return {
      steps: [
        command({ type: "CLEAR_SEEK" }),
        // Invalidate the old decoder before changing the server-offset URI.
        // A late fatal callback from that decoder is stale, not source failure.
        command({
          type: "RETRY_SOURCE",
          positionSeconds: target,
          continuePausedSession: false,
        }),
        lifecycle({ type: "RESET_MEDIA_CLOCK" }),
        lifecycle({ type: "SET_POSITION", value: target }),
        command({
          type: "SET_SERVER_SEEK",
          itemId: input.itemId,
          seconds: target,
        }),
        command({ type: "SHOW_CONTROLS" }),
        ...(input.reportIntent
          ? [command({ type: "SESSION_PROGRESS" })]
          : []),
      ],
    };
  }
  return {
    steps: [command({
      type: "SEEK_DECODER",
      seconds: target,
      reportIntent: input.reportIntent,
    })],
  };
}

export function planSuspendPlayback(input: {
  sessionStarted: boolean;
  paused: boolean;
}): DecoderEventDecision {
  if (!input.sessionStarted || input.paused) return ignoredDecision();
  return {
    steps: [
      lifecycle({ type: "SET_PAUSED", value: true }),
      command({ type: "SESSION_PROGRESS" }),
    ],
  };
}

export function planSetVolume(value: number): DecoderEventDecision {
  if (!Number.isFinite(value)) return ignoredDecision();
  const volume = Math.max(0, Math.min(1, value));
  return {
    steps: [
      lifecycle({ type: "SET_VOLUME", value: volume }),
      lifecycle({ type: "SET_MUTED", value: false }),
      command({ type: "SESSION_PROGRESS" }),
    ],
  };
}
